import { logWarn } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'openedLotCount';

/**
 * Parse `opened_lot_count` from a product payload.
 * Missing values are unknown. A present value that is not a finite number is malformed.
 *
 * @param {*} raw
 * @returns {{ value: number|null, malformed: boolean }}
 */
function parseOpenedLotCountField(raw) {
  if (raw === undefined || raw === null || raw === '') {
    return { value: null, malformed: false };
  }
  if (typeof raw === 'boolean') {
    return { value: null, malformed: true };
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n)) {
    return { value: null, malformed: true };
  }
  return { value: n, malformed: false };
}

/**
 * How many stock-item lots are opened (In Use).
 *
 * A present `items` array wins, including an empty array (0). That keeps the
 * inventory modal in sync when a lot is toggled without a refetch.
 * If `items` is missing, use `opened_lot_count` from the product list.
 * If both are missing, return null so the UI shows an em dash rather than 0.
 * The product list does not include lots today, so a missing array must not
 * be treated as zero opened lots.
 *
 * @param {{ items?: Array<{ in_use?: boolean }>|null, opened_lot_count?: * }|null|undefined} source
 * @returns {number|null}
 */
export function resolveOpenedLotCount(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  if (Array.isArray(source.items)) {
    return source.items.reduce(
      (count, item) => count + (item?.in_use ? 1 : 0),
      0,
    );
  }

  const parsed = parseOpenedLotCountField(source.opened_lot_count);
  if (parsed.malformed) {
    logWarn(LOG_CONTEXT, 'opened_lot_count is not a finite number', {
      opened_lot_count: source.opened_lot_count,
    });
    return null;
  }
  return parsed.value;
}

/**
 * Opened-lot count for a list row.
 *
 * A non-empty `items` array is counted directly. A missing array and an empty
 * array both fall through to `opened_lot_count`, so a list save cannot turn
 * “no lots on the row” into zero opened lots.
 *
 * @param {{ items?: Array<{ in_use?: boolean }>|null, opened_lot_count?: * }|null|undefined} product
 * @returns {number|null}
 */
export function listOpenedLotCountBase(product) {
  if (!product || typeof product !== 'object') {
    return null;
  }
  if (Array.isArray(product.items) && product.items.length > 0) {
    return resolveOpenedLotCount(product);
  }
  return resolveOpenedLotCount({ opened_lot_count: product.opened_lot_count });
}

/**
 * Move a known opened-lot count by one lot’s In Use change.
 * Unknown (`null`) stays unknown. The result is never negative.
 *
 * @param {number|null} currentCount
 * @param {boolean} previousInUse - Lot was in use before the save
 * @param {boolean} nextInUse - Lot is in use after the save
 * @returns {number|null}
 */
export function adjustOpenedLotCount(currentCount, previousInUse, nextInUse) {
  if (currentCount === null || currentCount === undefined) {
    return null;
  }
  const n = typeof currentCount === 'number' ? currentCount : Number(currentCount);
  if (!Number.isFinite(n)) {
    return null;
  }
  const delta = Number(Boolean(nextInUse)) - Number(Boolean(previousInUse));
  return Math.max(0, n + delta);
}

/**
 * Fields to write onto a product list row after a lot create, edit, or delete.
 *
 * Pass `items` only when the row already stores lots (or the server sent a
 * full replacement). Otherwise `includeItems` is false and the count moves
 * by the In Use delta. Callers must not attach `items: []` in that case.
 *
 * @param {object|null|undefined} product - List row before the save
 * @param {{ previousInUse?: boolean, nextInUse?: boolean, items?: Array<{ in_use?: boolean }>|null }} [change]
 * @returns {{ includeItems: boolean, items?: Array, opened_lot_count: number|null }}
 */
export function buildListOpenedLotPatch(product, change = {}) {
  if (Array.isArray(change.items)) {
    return {
      includeItems: true,
      items: change.items,
      opened_lot_count: resolveOpenedLotCount({ items: change.items }),
    };
  }

  const base = listOpenedLotCountBase(product);
  const hasTransition =
    change.previousInUse !== undefined && change.nextInUse !== undefined;
  return {
    includeItems: false,
    opened_lot_count: hasTransition
      ? adjustOpenedLotCount(base, change.previousInUse, change.nextInUse)
      : base,
  };
}
